import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClockCircleOutlined,
  DollarOutlined,
  TeamOutlined,
  CalendarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import {
  Progress,
  Table,
  DatePicker,
  Select,
  Modal,
  Button,
  message,
} from 'antd';
import TimeTrackingService from '@/services/timeTracking.service';
import { formatDuration } from '../../_shared/utils';

const { RangePicker } = DatePicker;

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function StatCard({ icon, label, value, sub, color = 'text-gray-700' }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border bg-white p-4">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`text-lg font-semibold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

export default function ProjectBudgetBar({
  budgetHours,
  trackedHours = 0,
  billableHours = 0,
  analytics = {},
  currency = '',
  projectId,
  tasks = [],
  _members = [],
}) {
  const { t } = useTranslation(['forms', 'messages']);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportDateRange, setExportDateRange] = useState(null);
  const [exportEmployee, setExportEmployee] = useState(null);
  const [exportTask, setExportTask] = useState(null);
  const [exporting, setExporting] = useState(false);

  const trackedMinutes = trackedHours * 60;
  const billableMinutes = billableHours * 60;
  const budgetMinutes = budgetHours ? budgetHours * 60 : 0;
  const percent = budgetHours
    ? Math.min(Math.round((trackedHours / budgetHours) * 100), 100)
    : 0;
  const isOver = budgetHours && trackedHours > budgetHours;
  const avgPerDay =
    analytics.activeDays > 0
      ? Math.round(trackedMinutes / analytics.activeDays)
      : 0;
  const approvalRate =
    trackedMinutes > 0
      ? Math.round(((analytics.approvedMinutes || 0) / trackedMinutes) * 100)
      : 0;

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = {};
      if (exportDateRange?.[0])
        params.dateFrom = exportDateRange[0].format('YYYY-MM-DD');
      if (exportDateRange?.[1])
        params.dateTo = exportDateRange[1].format('YYYY-MM-DD');
      if (exportEmployee) params.employee = exportEmployee;
      if (exportTask) params.task = exportTask;
      const blob = await TimeTrackingService.exportProjectAnalytics(
        projectId,
        params
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'project_analytics.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch {
      message.error(t('error', { ns: 'messages' }));
    } finally {
      setExporting(false);
    }
  }, [projectId, exportDateRange, exportEmployee, exportTask, t]);

  const handleExportClose = useCallback(() => {
    setExportOpen(false);
    setExportDateRange(null);
    setExportEmployee(null);
    setExportTask(null);
  }, []);

  const memberOptions = (analytics.perMember || []).map((m) => ({
    value: m._id,
    label: m.name,
  }));

  const taskOptions = tasks.map((tk) => ({
    value: tk._id,
    label: tk.name,
  }));

  const memberColumns = [
    {
      title: t('employee', { ns: 'forms' }),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('tracked', { ns: 'forms' }),
      dataIndex: 'totalMinutes',
      key: 'totalMinutes',
      render: (v) => formatDuration(v),
      sorter: (a, b) => a.totalMinutes - b.totalMinutes,
      defaultSortOrder: 'descend',
    },
    {
      title: t('billable', { ns: 'forms' }),
      dataIndex: 'billableMinutes',
      key: 'billableMinutes',
      render: (v) => formatDuration(v),
    },
    {
      title: t('entries', { ns: 'forms' }),
      dataIndex: 'entryCount',
      key: 'entryCount',
    },
    {
      title: '%',
      key: 'share',
      render: (_, r) =>
        trackedMinutes > 0
          ? `${Math.round((r.totalMinutes / trackedMinutes) * 100)}%`
          : '0%',
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Timeline + Export */}
      <div className="flex items-center justify-between rounded-lg border bg-white p-4 text-sm">
        <div className="flex items-center gap-6">
          {(analytics.firstEntry || analytics.lastEntry) && (
            <>
              <HistoryOutlined className="text-gray-400" />
              <span>
                {t('firstEntry', { ns: 'forms' })}:{' '}
                <strong>{formatDate(analytics.firstEntry)}</strong>
              </span>
              <span>
                {t('lastEntry', { ns: 'forms' })}:{' '}
                <strong>{formatDate(analytics.lastEntry)}</strong>
              </span>
              {analytics.firstEntry && analytics.lastEntry && (
                <span className="text-gray-400">
                  (
                  {Math.ceil(
                    (new Date(analytics.lastEntry) -
                      new Date(analytics.firstEntry)) /
                      (1000 * 60 * 60 * 24)
                  ) + 1}{' '}
                  {t('calendarDays', { ns: 'forms' })})
                </span>
              )}
            </>
          )}
        </div>
        <Button
          icon={<DownloadOutlined />}
          shape="round"
          className="btn-default"
          onClick={() => setExportOpen(true)}
        >
          {t('exportExcel', { ns: 'forms' })}
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <StatCard
          icon={<ClockCircleOutlined />}
          label={t('tracked', { ns: 'forms' })}
          value={formatDuration(trackedMinutes)}
          sub={
            avgPerDay > 0
              ? `~${formatDuration(avgPerDay)} / ${t('day', { ns: 'forms' })}`
              : undefined
          }
        />
        <StatCard
          icon={<DollarOutlined />}
          label={t('billable', { ns: 'forms' })}
          value={formatDuration(billableMinutes)}
          sub={
            trackedMinutes > 0
              ? `${Math.round((billableMinutes / trackedMinutes) * 100)}% ${t('ofTotal', { ns: 'forms' })}`
              : undefined
          }
          color="text-green-600"
        />
        <StatCard
          icon={<TeamOutlined />}
          label={t('activeMembers', { ns: 'forms' })}
          value={analytics.activeMembers || 0}
        />
        <StatCard
          icon={<CalendarOutlined />}
          label={t('activeDays', { ns: 'forms' })}
          value={analytics.activeDays || 0}
        />
        <StatCard
          icon={<FileTextOutlined />}
          label={t('entries', { ns: 'forms' })}
          value={analytics.totalEntries || 0}
        />
        <StatCard
          icon={<CheckCircleOutlined />}
          label={t('approvalRate', { ns: 'forms' })}
          value={`${approvalRate}%`}
          color={approvalRate === 100 ? 'text-green-600' : 'text-orange-500'}
        />
      </div>

      {/* Cost estimate */}
      {analytics.totalCost > 0 && (
        <div className="rounded-lg border bg-white p-4">
          <div className="text-xs text-gray-400">
            {t('estimatedCost', { ns: 'forms' })}
          </div>
          <div className="text-xl font-semibold text-blue-600">
            {analytics.totalCost.toLocaleString('sr-RS', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{' '}
            {currency}
          </div>
        </div>
      )}

      {/* Budget progress */}
      {budgetHours > 0 && (
        <div className="rounded-lg border bg-white p-6">
          <h4 className="mb-3 text-sm font-medium">
            {t('budget', { ns: 'forms' })}
          </h4>
          <div className="mb-2 flex justify-between text-sm">
            <span>
              {formatDuration(trackedMinutes)} / {formatDuration(budgetMinutes)}
            </span>
            <span>{percent}%</span>
          </div>
          <Progress
            percent={percent}
            showInfo={false}
            status={isOver ? 'exception' : 'active'}
            strokeColor={isOver ? '#ff4d4f' : '#1890ff'}
          />
          {isOver && (
            <div className="mt-2 text-sm text-red-500">
              {t('overBudget', { ns: 'forms' })}: +
              {formatDuration((trackedHours - budgetHours) * 60)}
            </div>
          )}
          {budgetHours > 0 && !isOver && (
            <div className="mt-2 text-xs text-gray-400">
              {t('remaining', { ns: 'forms' })}:{' '}
              {formatDuration((budgetHours - trackedHours) * 60)}
            </div>
          )}
        </div>
      )}

      {/* Status breakdown */}
      {trackedMinutes > 0 && (
        <div className="rounded-lg border bg-white p-6">
          <h4 className="mb-3 text-sm font-medium">
            {t('statusBreakdown', { ns: 'forms' })}
          </h4>
          <div className="flex gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
              <span>
                {t('APPROVED', { ns: 'forms' })}:{' '}
                {formatDuration(analytics.approvedMinutes || 0)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-orange-400" />
              <span>
                {t('SUBMITTED', { ns: 'forms' })}:{' '}
                {formatDuration(analytics.submittedMinutes || 0)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-gray-400" />
              <span>
                {t('DRAFT', { ns: 'forms' })}:{' '}
                {formatDuration(analytics.draftMinutes || 0)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Per-member breakdown */}
      {analytics.perMember?.length > 0 && (
        <div className="rounded-lg border bg-white p-6">
          <h4 className="mb-3 text-sm font-medium">
            {t('timePerMember', { ns: 'forms' })}
          </h4>
          <Table
            dataSource={analytics.perMember}
            columns={memberColumns}
            rowKey="_id"
            pagination={false}
            size="small"
          />
        </div>
      )}

      {/* Export modal */}
      <Modal
        title={t('exportExcel', { ns: 'forms' })}
        open={exportOpen}
        onCancel={handleExportClose}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button shape="round" onClick={handleExportClose}>
              {t('cancel', { ns: 'forms' })}
            </Button>
            <Button
              type="primary"
              shape="round"
              className="btn-primary"
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={handleExport}
            >
              {t('exportExcel', { ns: 'forms' })}
            </Button>
          </div>
        }
        width={480}
      >
        <div className="flex flex-col gap-4">
          <div>
            <label className="mb-1 block text-sm text-gray-500">
              {t('dateRange', { ns: 'forms' })}
            </label>
            <RangePicker
              format="DD-MM-YYYY"
              value={exportDateRange}
              onChange={setExportDateRange}
              allowClear
              placeholder={[
                t('dateFrom', { ns: 'forms' }),
                t('dateTo', { ns: 'forms' }),
              ]}
              style={{ width: '100%' }}
            />
          </div>
          {memberOptions.length > 0 && (
            <div>
              <label className="mb-1 block text-sm text-gray-500">
                {t('employee', { ns: 'forms' })}
              </label>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('allMembers', { ns: 'forms' })}
                options={memberOptions}
                value={exportEmployee}
                onChange={setExportEmployee}
                style={{ width: '100%' }}
              />
            </div>
          )}
          {taskOptions.length > 0 && (
            <div>
              <label className="mb-1 block text-sm text-gray-500">
                {t('task', { ns: 'forms' })}
              </label>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('allTasks', { ns: 'forms' })}
                options={taskOptions}
                value={exportTask}
                onChange={setExportTask}
                style={{ width: '100%' }}
              />
            </div>
          )}
          <p className="text-xs text-gray-400">
            {t('exportHint', { ns: 'forms' })}
          </p>
        </div>
      </Modal>
    </div>
  );
}
